import '../ActivityActions.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AttachFilesBlock, IAttachFilesBlockRefActions } from '@molecules/AttachFilesBlock/AttachFilesBlock';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { checkAttachValidation } from '@utils/documentUtils';
import { getActivityIdFromCompositeId } from '@utils/helpers';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { getUploadingFileLabel } from '@utils/uploadProgress';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { stringifyError } from '@srm/core/errors';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { hasSelectedFiles, skipUploadedFiles, UPLOADED_FILES_WARNING } from '@organisms/actions/skipUploadedFiles';
import { IBaseApproveProps } from './IBaseApproveProps';

interface IBaseApproveFormData {
    comment?: string;
}

export const BaseApproveModal: React.FC<IBaseApproveProps> = (props: IBaseApproveProps) => {
    const childFilesBlock = useRef<IAttachFilesBlockRefActions | null>(null);
    const [attachesCreateModel, setAttachesCreateModel] = useState<IAttachesCreateModel>();
    const [filesSizeExceeded, setFilesSizeExceeded] = useState<boolean>(false);

    const [errorText, setErrorText] = useState<string>();
    const [warningText, setWarningText] = useState<string>();
    // Подготовка (модель действия) и выполнение — разные состояния: подготовку можно бросить
    // закрытием окна, выполнение — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>();
    const [submitButtonLabel, setSubmitButtonLabel] = useState<string>();
    const uploadedFiles = useRef(new Set<File>());

    const commentIsRequired = props.approveModel.commentIsRequired;

    const formMethods = useForm<IBaseApproveFormData>({ mode: 'onBlur' });
    const hasEditedComment = useUnsavedFormInput(formMethods, { textFields: ['comment'] });

    useEffect(() => {
        // Закрытое окно ответы подготовки не применяет.
        let ignore = false;
        setLoading(true);
        const activityExecutionService = new ActivityExecutionService();
        activityExecutionService
            .getActivityActionModel(props.actionKey, props.activityId)
            .then((res) => {
                if (ignore) return;
                formMethods.reset({ comment: res.data.prefilledComment ?? '' });
            })
            .catch((error: unknown) => {
                if (!ignore) setErrorText(stringifyError(error));
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });

        //получение модели для аттачей
        if (props.attachFiles) {
            const attachServ = new AttachActionsService(props.docId, '-1');
            attachServ
                .getAttachInfo(props.actionKey, props.activityId)
                .then((res) => {
                    if (!ignore) setAttachesCreateModel(res.data);
                })
                .catch((error: unknown) => {
                    if (!ignore) setErrorText(stringifyError(error));
                });
        }

        return () => {
            ignore = true;
        };
    }, []);

    const resetSubmitState = () => {
        setIsSubmitting(false);
        setUploadProgress(undefined);
        setSubmitButtonLabel(undefined);
    };

    const onSubmit = async (form: IBaseApproveFormData) => {
        setErrorText(undefined);
        setWarningText(undefined);

        if (!props.objId) return;

        let filesToSend: ISelectedFiles = { files: [], attachProperties: [] };

        if (attachesCreateModel && childFilesBlock) {
            if (!childFilesBlock?.current?.isValid()) return;
            const f = childFilesBlock?.current?.getData();
            if (f) filesToSend = f;
        }

        // проверка на обязательность наличия аттачей
        if (
            props.attachFiles &&
            props.attachRequired &&
            (filesToSend?.files === undefined || filesToSend?.files.length === 0)
        ) {
            setWarningText('Необходимо приложить файл');
            return;
        }
        if (props.attachRequiredValidation && props.attachRequiredValidation.length > 0) {
            const checkResult = checkAttachValidation(props.attachRequiredValidation, filesToSend);
            if (checkResult.length > 0) {
                setWarningText(checkResult.join('\n'));
                return;
            }
        }

        setIsSubmitting(true);
        const attachServ = new AttachActionsService(props.docId, '-1');

        try {
            const pendingFiles = skipUploadedFiles(filesToSend, uploadedFiles.current);
            const hasFiles = filesToSend.files.length > 0;

            if (pendingFiles.files.length > 0) {
                setUploadProgress(0);
                await attachServ.createAttaches(pendingFiles, (state) => {
                    setUploadProgress(state.progress);
                    setSubmitButtonLabel(state.progress >= 100 ? 'Выполняю действие...' : getUploadingFileLabel(state));
                });
                pendingFiles.files.forEach((file) => uploadedFiles.current.add(file));
            }

            if (hasFiles) {
                setUploadProgress(100);
                setSubmitButtonLabel('Выполняю действие...');
            }

            const actId = getActivityIdFromCompositeId(props.objId);

            const activityExecutionService = new ActivityExecutionService();
            await activityExecutionService.activityAction(
                props.actionKey,
                actId,
                props.approveModel.actType,
                form.comment,
            );

            resetSubmitState();
            if (props.completeHandler) props.completeHandler(true);
        } catch (e) {
            setErrorText(stringifyError(e));
            if (uploadedFiles.current.size > 0) setWarningText(UPLOADED_FILES_WARNING);
            console.error(e);
            resetSubmitState();
        }
    };

    const modalContainerProps: IModalContainerProps = {
        header: props.displayName,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading || (isSubmitting && uploadProgress === undefined),
        progress: uploadProgress,
        progressLabel: submitButtonLabel,
        closeDisabled: isSubmitting,
        hasUnsavedInput: () => hasEditedComment() || hasSelectedFiles(childFilesBlock.current),
        okButtonDisabled: filesSizeExceeded,
        errorText: errorText,
        warningText: warningText,
        formMethods: formMethods,
        onSubmit: onSubmit,
        onCancel: () => {
            if (props?.completeHandler) props?.completeHandler(false);
        },
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <TextareaControl
                name="comment"
                label="Комментарий"
                required={commentIsRequired}
                initialRowCount={8}
                control={formMethods.control}
                rules={commentIsRequired ? requiredTextRules() : undefined}
            />
            {attachesCreateModel && (
                <AttachFilesBlock
                    ref={childFilesBlock}
                    model={attachesCreateModel}
                    onChangeFilesSizeExceeded={(value) => {
                        setFilesSizeExceeded(value);
                    }}
                />
            )}
        </ModalContainer>
    );
};
